import { ref, watch } from 'vue';
import AgentsAPI from 'dashboard/api/agents';
import PostSaleCasesAPI from 'dashboard/api/postSaleCases';

const messageFrom = (error, fallback) =>
  error?.response?.data?.message || error?.response?.data?.error || fallback;

export function usePostSaleSalesDetailActions({ postSaleCase, t, notify }) {
  const agents = ref([]);
  const assigning = ref(false);
  const cancelling = ref(false);
  const completing = ref(false);
  const loadingAgents = ref(false);
  let agentsRequestSequence = 0;

  const canAssign = () => Boolean(postSaleCase.value?.permissions?.can_assign);

  const loadAgents = async () => {
    agentsRequestSequence += 1;
    const sequence = agentsRequestSequence;
    agents.value = [];
    if (!canAssign()) return [];

    loadingAgents.value = true;
    try {
      const { data } = await AgentsAPI.get();
      if (sequence !== agentsRequestSequence) return [];

      const currentId = postSaleCase.value?.responsible?.id;
      agents.value = (data?.payload || data || []).filter(
        agent => String(agent.id) !== String(currentId || '')
      );
      return agents.value;
    } catch (error) {
      if (sequence === agentsRequestSequence) {
        notify(messageFrom(error, t('POST_SALE.ALERTS.ACTION_ERROR')));
      }
      return [];
    } finally {
      if (sequence === agentsRequestSequence) loadingAgents.value = false;
    }
  };

  const assign = async responsibleUserId => {
    if (!canAssign() || !responsibleUserId) return null;

    assigning.value = true;
    try {
      const { data } = await PostSaleCasesAPI.assign(postSaleCase.value.id, {
        responsible_user_id: responsibleUserId,
      });
      postSaleCase.value = data;
      notify(t('POST_SALE.ALERTS.RESPONSIBLE_UPDATED'));
      return data;
    } catch (error) {
      notify(messageFrom(error, t('POST_SALE.ALERTS.ACTION_ERROR')));
      return null;
    } finally {
      assigning.value = false;
    }
  };

  const cancel = async ({ cancellationDate, reason } = {}) => {
    const canCancel =
      postSaleCase.value?.permissions?.can_cancel ??
      postSaleCase.value?.permissions?.can_manage;
    if (!canCancel || !cancellationDate || !reason?.trim()) {
      return null;
    }

    cancelling.value = true;
    try {
      const { data } = await PostSaleCasesAPI.cancel(postSaleCase.value.id, {
        data_cancelamento: cancellationDate,
        reason: reason.trim(),
      });
      postSaleCase.value = data;
      notify(t('POST_SALE.ALERTS.CANCELED'));
      return data;
    } catch (error) {
      notify(messageFrom(error, t('POST_SALE.ALERTS.ACTION_ERROR')));
      return null;
    } finally {
      cancelling.value = false;
    }
  };

  const complete = async () => {
    const canComplete =
      postSaleCase.value?.permissions?.can_complete ??
      postSaleCase.value?.permissions?.can_manage;
    if (!canComplete) return null;

    completing.value = true;
    try {
      const { data } = await PostSaleCasesAPI.complete(postSaleCase.value.id);
      postSaleCase.value = data;
      notify(t('POST_SALE.ALERTS.COMPLETED'));
      return data;
    } catch (error) {
      notify(messageFrom(error, t('POST_SALE.ALERTS.ACTION_ERROR')));
      return null;
    } finally {
      completing.value = false;
    }
  };

  watch(
    () => [
      postSaleCase.value?.id,
      postSaleCase.value?.responsible?.id,
      postSaleCase.value?.permissions?.can_assign,
    ],
    loadAgents,
    { immediate: true }
  );

  return {
    agents,
    assign,
    assigning,
    cancel,
    cancelling,
    complete,
    completing,
    loadingAgents,
  };
}
